/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {LazyOutlet} from './SuspensedView'
import {lazyWithReload} from './lazyWithReload'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import PrivacyPolicy from '../modules/PrivacyPolicy'
import { KioskPage } from '../pages/kiosk/KioskPage'
import { KioskLayout } from '../pages/kiosk/KioskLayout'

// The kiosk pages are the same code-split chunks the private routes load
// (ADR-0022); an eager import here would pull them back into the main bundle.
const Productions = lazyWithReload(() => import('../pages/production/productions'))
const StocksPage = lazyWithReload(() => import('../pages/stock-management/stock/stocks'))
const StockPage = lazyWithReload(() => import('../pages/stock-management/stock/stock'))
// The logged-out document pages (ADR-0046): code-split, never in the main bundle.
const PublicInvoicePage = lazyWithReload(() => import('../pages/public/PublicInvoicePage'))
const PublicQuotePage = lazyWithReload(() => import('../pages/public/PublicQuotePage'))

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path='privacy-policy' element={<PrivacyPolicy />} />
          <Route element={<LazyOutlet />}>
            <Route path='public/invoices/:token' element={<PublicInvoicePage />} />
            <Route path='public/quotes/:token' element={<PublicQuotePage />} />
          </Route>
          <Route path='kiosk' element={<KioskPage />} />
          {currentUser ? (
            <>
              {/* Kiosk layout routes — no sidebar, minimal top bar */}
              <Route path='kiosk' element={<KioskLayout />}>
                <Route element={<LazyOutlet />}>
                  <Route path='production' element={<Productions />} />
                  <Route path='stocks' element={<StocksPage />} />
                  <Route path='stocks/:id' element={<StockPage />} />
                </Route>
              </Route>

              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
